/**
 * Reading a long text a page at a time, without losing your place.
 *
 * Two overlays do this now — the session's transcript and, once a session has
 * ended, its recording — and the interesting part is not the fetching. It is
 * `loadOlder`: an operator who asks for more history is asking for *more*, not
 * for a different place in it, so the view has to end up exactly where it was
 * with the new lines above it. The anchor is the scroll height rather than a line
 * index, because the list is plain DOM and "how much taller did it get" is exact
 * and needs no measurement of individual rows.
 *
 * Extracted rather than copied, and for the reason the wall tiles were: that
 * arithmetic is subtle, a second copy of it rots quietly, and the failure is a
 * reader silently jumping somewhere else in a file they were half way through.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { api } from '@/api/client'

/** What both endpoints answer with. Each adds its own fields on top. */
export interface LineWindow {
  lines: string[]
  /** The first line this window holds, and the line after its last. */
  from: number
  next: number
  /** The extent that exists, which is what says whether there is more above. */
  first: number
  end: number
  /** The session's current screen, where there is one. */
  screen?: string[]
}

export interface WindowedLines<W extends LineWindow> {
  /** The most recent window, carrying whatever else its endpoint said. */
  window: W | null
  /** Everything loaded so far, oldest first. */
  lines: string[]
  loading: boolean
  error: string | null
  /** Whether there is a page before the one held. */
  hasOlder: boolean
  loadOlder: () => Promise<void>
  /** The element to attach to the scrolling list. */
  scrollRef: React.RefObject<HTMLDivElement | null>
}

export function useWindowedLines<W extends LineWindow>(
  path: string,
  pageLines: number,
): WindowedLines<W> {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [window_, setWindow] = useState<W | null>(null)
  const [lines, setLines] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const busyRef = useRef(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await api.get<W>(path, { limit: pageLines })
      setWindow(data)
      setLines(data.lines)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }, [path, pageLines])

  const loadOlder = useCallback(async () => {
    const el = scrollRef.current
    const current = window_
    if (!el || !current || busyRef.current || current.from <= current.first) return
    busyRef.current = true
    setLoading(true)
    setError(null)
    const from = Math.max(current.first, current.from - pageLines)
    try {
      const data = await api.get<W>(path, { from, limit: current.from - from })
      const before = el.scrollHeight
      setLines((prev) => [...data.lines, ...prev])
      setWindow({ ...data, next: current.next })
      // After the browser has laid the new lines out, put the view back where it
      // was.
      requestAnimationFrame(() => {
        el.scrollTop += el.scrollHeight - before
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      busyRef.current = false
      setLoading(false)
    }
  }, [path, pageLines, window_])

  useEffect(() => {
    void load()
  }, [load])

  // Opened on the newest line, which is what an operator wants to see first.
  // Only when the first page lands: later pages are prepended above and must not
  // move the view.
  const empty = lines.length === 0
  useEffect(() => {
    const el = scrollRef.current
    if (el && !empty && el.scrollTop === 0) el.scrollTop = el.scrollHeight
  }, [empty])

  return {
    window: window_,
    lines,
    loading,
    error,
    hasOlder: window_ != null && window_.from > window_.first,
    loadOlder,
    scrollRef,
  }
}
